import { ArquivoDTO } from "@/types/arquivo.type";
import { TipoArquivo } from "@workflow/types";
import axios, { AxiosError } from "axios";
import { toast } from "sonner";
import { httpClient } from "./httpClient";

/** Callback de progresso do upload (0–100). Usado pela UX de progresso (Fase 3). */
export type UploadProgressCallback = (percent: number) => void;

interface UploadUrlResponse {
  uploadUrl: string;
  headers: Record<string, string>;
  ticket: string;
}

/** Passo em que o fluxo de upload direto falhou — anexado ao erro p/ log/diagnóstico. */
type UploadStep = "upload-url" | "put-storage" | "confirm";

/**
 * Abaixo deste tamanho o fluxo multipart legado ainda funciona (fica sob o teto de
 * ~32 MB do Cloud Run) e serve de fallback. Acima disso só o fluxo de signed URL
 * dá conta, então uma falha nele é surfaçada ao usuário.
 */
const LEGACY_FALLBACK_MAX_BYTES = 30 * 1024 * 1024;

class ArquivoService {
  private readonly basePath = "api/v1/arquivos";

  public async getArquivoById(id: number): Promise<ArquivoDTO> {
    return await httpClient.get<ArquivoDTO>(`${this.basePath}/${id}`);
  }

  public async downloadArquivo(id: number): Promise<string> {
    return await httpClient.get<string>(`${this.basePath}/${id}/download`);
  }

  /**
   * Sobe um arquivo direto pro storage (signed URL, 3 passos). Assinatura preservada;
   * `onProgress` é opcional (usado só pela Fase 3). Em falha, cai no multipart legado
   * para arquivos pequenos — o usuário não percebe diferença do fluxo de hoje.
   */
  public async uploadArquivo(
    tipo: TipoArquivo,
    file: File,
    versaoId?: number,
    onProgress?: UploadProgressCallback,
  ): Promise<ArquivoDTO> {
    if (!file) {
      return null as unknown as ArquivoDTO;
    }

    try {
      const { uploadUrl, headers, ticket } = await this.requestUploadUrl(tipo, file, versaoId);
      await this.putToSignedUrl(uploadUrl, file, headers, onProgress);
      return await this.confirmUpload(ticket);
    } catch (error) {
      return this.handleUploadFailure(error, file, () => this.legacyUpload(tipo, file, versaoId));
    }
  }

  /** Substitui um arquivo existente (mesmo fluxo/fallback do upload). */
  public async replaceArquivo(
    id: number,
    file: File,
    onProgress?: UploadProgressCallback,
  ): Promise<ArquivoDTO> {
    if (!file) {
      return null as unknown as ArquivoDTO;
    }

    try {
      const { uploadUrl, headers, ticket } = await this.requestReplaceUrl(id, file);
      await this.putToSignedUrl(uploadUrl, file, headers, onProgress);
      return await this.confirmUpload(ticket);
    } catch (error) {
      return this.handleUploadFailure(error, file, () => this.legacyReplace(id, file));
    }
  }

  // --- Passos do fluxo de signed URL ---------------------------------------------

  private async requestUploadUrl(
    tipo: TipoArquivo,
    file: File,
    versaoId?: number,
  ): Promise<UploadUrlResponse> {
    try {
      return await httpClient.post<UploadUrlResponse>(
        `${this.basePath}/upload-url/${tipo}${versaoId ? `?versao-id=${versaoId}` : ""}`,
        this.buildMetadata(file),
        { toastConfig: { showError: false } },
      );
    } catch (error) {
      throw this.tagStep("upload-url", error);
    }
  }

  private async requestReplaceUrl(id: number, file: File): Promise<UploadUrlResponse> {
    try {
      return await httpClient.post<UploadUrlResponse>(
        `${this.basePath}/${id}/replace-url`,
        this.buildMetadata(file),
        { toastConfig: { showError: false } },
      );
    } catch (error) {
      throw this.tagStep("upload-url", error);
    }
  }

  /**
   * PUT dos bytes direto no storage. Usa uma instância axios **crua** (não o
   * `httpClient`): os interceptors injetam `Authorization: Bearer`, o que quebraria a
   * assinatura, e o `timeout: 60000` mataria arquivos grandes em conexão ruim.
   */
  private async putToSignedUrl(
    uploadUrl: string,
    file: File,
    headers: Record<string, string>,
    onProgress?: UploadProgressCallback,
  ): Promise<void> {
    try {
      await axios.put(uploadUrl, file, {
        headers,
        timeout: 0,
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
      });
    } catch (error) {
      throw this.tagStep("put-storage", error);
    }
  }

  private async confirmUpload(ticket: string): Promise<ArquivoDTO> {
    try {
      return await httpClient.post<ArquivoDTO>(
        `${this.basePath}/confirm`,
        { ticket },
        { toastConfig: { showError: false } },
      );
    } catch (error) {
      throw this.tagStep("confirm", error);
    }
  }

  // --- Fallback multipart legado --------------------------------------------------

  private async legacyUpload(
    tipo: TipoArquivo,
    file: File,
    versaoId?: number,
  ): Promise<ArquivoDTO> {
    const formData = new FormData();
    formData.append("file", file);

    return await httpClient.post<ArquivoDTO>(
      `${this.basePath}/upload/${tipo}${versaoId ? `?versao-id=${versaoId}` : ""}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  }

  private async legacyReplace(id: number, file: File): Promise<ArquivoDTO> {
    const formData = new FormData();
    formData.append("file", file);

    return await httpClient.put<ArquivoDTO>(`${this.basePath}/${id}/replace`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  // --- Helpers --------------------------------------------------------------------

  private buildMetadata(file: File) {
    return {
      nome: file.name,
      mimetype: file.type || "application/octet-stream",
      tamanho: file.size,
    };
  }

  /** Loga o passo que falhou (com status HTTP) e anexa o passo ao erro. */
  private tagStep(step: UploadStep, error: unknown): unknown {
    const status = (error as AxiosError)?.response?.status;
    console.error(`[ArquivoService] Falha no passo "${step}" do upload direto`, { status, error });
    if (error && typeof error === "object") {
      (error as { uploadStep?: UploadStep }).uploadStep = step;
    }
    return error;
  }

  /**
   * Rede de segurança: arquivos pequenos caem no multipart legado (o usuário não
   * percebe). Arquivos grandes que só o fluxo novo suporta surfaçam erro claro.
   */
  private async handleUploadFailure(
    error: unknown,
    file: File,
    legacyFn: () => Promise<ArquivoDTO>,
  ): Promise<ArquivoDTO> {
    const step = (error as { uploadStep?: UploadStep })?.uploadStep ?? "desconhecido";

    if (file.size <= LEGACY_FALLBACK_MAX_BYTES) {
      console.warn(
        `[ArquivoService] Upload direto falhou no passo "${step}"; usando fallback multipart legado.`,
      );
      return await legacyFn();
    }

    console.error(
      `[ArquivoService] Upload direto falhou no passo "${step}" e o arquivo é grande demais para o fallback.`,
    );
    toast.error(`Não foi possível enviar o arquivo (falha em: ${step}). Tente novamente.`);
    throw error;
  }
}

export const arquivoService = new ArquivoService();
